import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import CallCard from "@/components/CallCard";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CATEGORIES } from "@/lib/brand";
import { Search as SearchIcon } from "lucide-react";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [type, setType] = useState("calls");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [results, setResults] = useState({ calls: [], people: [] });

  const run = () => {
    const qs = new URLSearchParams({ q, type });
    if (category !== "all") qs.set("category", category);
    if (status !== "all") qs.set("status", status);
    api.get(`/search?${qs.toString()}`).then(({ data }) => setResults((r) => ({ ...r, ...data }))).catch(() => {});
    setParams({ q });
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, [type, category, status]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <h1 className="font-display text-3xl font-bold mb-4">Search</h1>
      <div className="flex gap-2 mb-4">
        <div className="flex-1 flex items-center border-2 border-ink bg-white h-12 px-3 shadow-brutal-sm">
          <SearchIcon className="w-5 h-5 mr-2 text-muted-foreground" />
          <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()} placeholder="Search Calls or people…"
            className="flex-1 outline-none bg-transparent" />
        </div>
        <button data-testid="search-btn" onClick={run} className="px-5 border-2 border-ink bg-brand text-white font-bold btn-press shadow-brutal-sm">Go</button>
      </div>

      <Tabs value={type} onValueChange={setType} className="mb-4">
        <TabsList className="border-2 border-ink bg-white">
          <TabsTrigger value="calls" data-testid="search-tab-calls">Calls</TabsTrigger>
          <TabsTrigger value="people" data-testid="search-tab-people">People</TabsTrigger>
        </TabsList>
      </Tabs>

      {type === "calls" && (
        <div className="flex gap-2 mb-4">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="border-2 border-ink h-9"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All categories</SelectItem>{CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="border-2 border-ink h-9"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="called_it">Called It</SelectItem>
              <SelectItem value="missed_it">Missed It</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {type === "calls" ? (
        <div className="space-y-5">
          {results.calls?.map((c) => <CallCard key={c.short_id} call={c} />)}
          {(!results.calls || results.calls.length === 0) && <p className="text-muted-foreground text-center py-8">No Calls found.</p>}
        </div>
      ) : (
        <div className="space-y-3">
          {results.people?.map((u) => (
            <Link key={u.username} to={`/@${u.username}`} data-testid={`person-${u.username}`} className="card-brutal p-3 bg-white flex items-center gap-3 hover:bg-secondary">
              <Avatar className="w-10 h-10 border-2 border-ink"><AvatarImage src={u.avatar_url} /><AvatarFallback className="bg-locked">{(u.display_name || "U")[0]}</AvatarFallback></Avatar>
              <div><div className="font-bold">{u.display_name}</div><div className="text-xs text-muted-foreground font-mono">@{u.username}</div></div>
            </Link>
          ))}
          {(!results.people || results.people.length === 0) && <p className="text-muted-foreground text-center py-8">No people found.</p>}
        </div>
      )}
    </div>
  );
}
